import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../features/inbox/inbox-2b.css';
import { useConversationEvents } from '../features/conversations/hooks/use-conversation-events';
import {
  buildLoadedPrefix,
  buildPageScopeLabel,
  resolveInboxDefaultSort,
} from '../features/inbox/inbox-defaults';
import { InboxBanners } from '../features/inbox/InboxBanners';
import { InboxConvList } from '../features/inbox/InboxConvList';
import { InboxFiltersRail } from '../features/inbox/InboxFiltersRail';
import { InboxPersonSearchResults } from '../features/inbox/InboxPersonSearchResults';
import { InboxTabs } from '../features/inbox/InboxTabs';
import { InboxToolbar, buildActiveFilterLabels } from '../features/inbox/InboxToolbar';
import { applyTabOverride, matchesTab } from '../features/inbox/tab-counts';
import { useAuth } from '../hooks/use-auth';
import { useCapability } from '../hooks/use-capability';
import { useConversationCounts, useConversations } from '../hooks/use-conversations';
import { useInboxFilters } from '../hooks/use-inbox-filters';
import { api } from '../lib/api-client';
import { useToastStore } from '../stores/toast-store';

import type { InboxEmptyState } from '../features/inbox/InboxConvList';
import type { InboxTabCounts } from '../features/inbox/InboxTabs';
import type { InboxTab } from '../features/inbox/types';
import type { ConversationListItem, ConversationsFilters, EscalationState } from '../lib/types';

/**
 * InboxPage — rota `/inbox` (Brief 2B T1).
 *
 * Inbox completa do atendente com:
 *   - Cabeçalho (título + sub)
 *   - Banners contextuais (P1 / ConflictAlert / LGPD)
 *   - 3 abas (Aguardando minha ação / IA dirigindo / Todas)
 *   - Toolbar (busca + ordenação + filtros ativos)
 *   - Lista com seções "Em atendimento" / "Em espera"
 *   - Filter rail à direita (filtros granulares Brief 2B)
 *
 * Filtros derivados do URL via `useInboxFilters`.
 * Abas sobrepõem preset de filtros via `applyTabOverride` (tab=awaiting-me →
 * assignedToUserId=me, tab=ai-driving → operationMode=H_c, tab=all →
 * status=any — sentinel "sem cláusula de status", Rodada 3 Task 4).
 *
 * Partição atendendo/espera client-side (heurística):
 *   - "Em atendimento": status=open/awaiting-attendant, assignedToUserId=caller
 *   - "Em espera": todos os outros
 *
 * Item candidato: endpoint server-side para partição semântica atendendo/espera
 * (groupBy=inbox-section) não existe ainda — registrado como gap no PR body.
 */
export function InboxPage(): JSX.Element {
  // Alvo B round2 — o default de ordenação é recência quando a cap opt-in
  // `inbox-recency-default` chega ao user; `?sort=` explícito sempre vence.
  // Wiring extraído para helper puro (inbox-defaults.spec pina o gate).
  const recencyDefault = useCapability('inbox-recency-default');
  const { filters, setFilter, clearFilters, setTab, currentTab, sortOption, setSortOption } =
    useInboxFilters({ defaultSort: resolveInboxDefaultSort(recencyDefault) });
  const navigate = useNavigate();
  // B1 (spec 2026-08-12 §3.3) — cap opt-in FORA de pacote: só chega ao user
  // via additionalCapabilities/CapabilityEntry (ativação = decisão do TL).
  const groupByPersonEnabled = useCapability('inbox-person-grouping');
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  const [esperaCollapsed, setEsperaCollapsed] = useState(true);

  // Fricção medida no teste do TL (12/Ago): quem BUSCA está procurando — o
  // resultado não pode nascer escondido atrás do colapso da «Em espera».
  // Busca ativa ⇒ auto-expande (o operador ainda pode re-colapsar à mão).
  const activeSearch = filters.search ?? '';
  useEffect(() => {
    if (activeSearch !== '') setEsperaCollapsed(false);
  }, [activeSearch]);

  // Lote Claim a partir da fila — atendente "pega" conversa órfã (sem dono).
  // A API de claim é atômica (corrida → 409); on success invalida a listagem
  // (a conversa migra para "Minhas") e navega ao detalhe já como dono (sem
  // o 403 not-assigned que travaria a abertura direta da órfã).
  const queryClient = useQueryClient();
  const claimMutation = useMutation({
    mutationFn: (id: string) => api.claimConversation(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Conversa assumida',
        description: 'Você agora é o atendente desta conversa.',
        durationMs: 3000,
      });
      navigate(`/atendimento/conversation/${id}`);
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Falha ao assumir conversa',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  // Filtros do usuário SEM o override de aba — base da query da aba ativa e
  // da query de contagens (badges das abas).
  const baseFilters = useMemo<ConversationsFilters>(() => {
    const base: ConversationsFilters = {};

    if (filters.status) base.status = filters.status;
    if (filters.channel) base.channel = filters.channel;
    if (filters.operationMode) base.operationMode = filters.operationMode;
    if (filters.assignedToUserId) base.assignedToUserId = filters.assignedToUserId;
    if (filters.search) base.search = filters.search;
    if (filters.cursor) base.cursor = filters.cursor;
    if (filters.limit) base.limit = filters.limit;

    // Filtros B3 (Brief 2B PR #304) — mapeiam para os params do list-conversations.dto.
    // 'system' (awaiting) / 'pending'|'none' (commitment) / intentFilter / inGroup
    // são honest-defer: não há param equivalente no backend ainda.
    if (filters.awaitingParty === 'customer' || filters.awaitingParty === 'operator') {
      base.awaitingPartyType = filters.awaitingParty;
    }
    if (filters.metaSessionType) base.metaSessionType = filters.metaSessionType;
    if (filters.sequenceFlag) base.hasActiveSequence = true;
    if (filters.commitmentFilter === 'broken') base.hasOverdueCommitment = true;

    // Ordenação: sempre envia a opção resolvida (a UI omite o param quando
    // 'urgency' default, mas o backend precisa do valor explícito para ordenar).
    base.sort = sortOption;

    return base;
  }, [filters, sortOption]);

  // Query da aba ativa: base + override da aba (extraído para
  // `applyTabOverride` — testável isoladamente em tab-counts.spec.ts).
  const effectiveFilters = useMemo<ConversationsFilters>(
    () => applyTabOverride(baseFilters, currentTab),
    [baseFilters, currentTab],
  );

  const { data, isLoading, isError, refetch } = useConversations(effectiveFilters);
  // Contagens agregadas no banco (badges das abas + por status do rail) —
  // contagem client-side sobre página parcial divergia da lista.
  const { data: counts } = useConversationCounts(baseFilters);

  // Subscribe to WS events to auto-invalidate list
  useConversationEvents(undefined);

  // Filtro explícito de status no rail vence a exclusão de terminais das abas
  // de ação (usuário pedindo "Encerrada" dentro de "Aguardando minha ação"
  // deve ver encerradas).
  const includeTerminal = filters.status !== undefined;

  const conversations: ConversationListItem[] = useMemo(() => {
    const raw: ConversationListItem[] = data?.data ?? [];
    return raw.filter((c) => matchesTab(c, currentTab, currentUserId, { includeTerminal }));
  }, [data, currentTab, currentUserId, includeTerminal]);

  // Client-side partition: atendendo vs espera
  // Heurística: "Em atendimento" = status in-progress/open/awaiting-attendant + assigned-to-me
  // Caveat: sem groupBy server-side, partição é visual. Endpoint futuro (Item candidato).
  //
  // Items 280+281 (T B11) — sort hint na fila de espera:
  // Conversas com escalationState='timed_out_passive' sobem ao topo da "Fila pendente"
  // pois representam clientes que não responderam ao pedido de escalation (prioritários).
  // Secondary sort preserva updatedAt desc (ordem original do servidor).
  const { atendendo, espera } = useMemo(() => {
    const ACTIVE_STATUSES = new Set(['in-progress', 'open', 'awaiting-attendant']);
    const a: ConversationListItem[] = [];
    const e: ConversationListItem[] = [];
    for (const c of conversations) {
      if (ACTIVE_STATUSES.has(c.status)) {
        a.push(c);
      } else {
        e.push(c);
      }
    }
    // Sort hint: timed_out_passive E (S7 PR2) awaiting_claim vão ao topo da fila
    // — o segundo é a fila SEGURADA pela IA (modo recepcionista S4), que o pool
    // precisa ver com prioridade igual à do cliente que ficou sem resposta.
    const isTopEscalation = (s: EscalationState | undefined): boolean =>
      s === 'timed_out_passive' || s === 'awaiting_claim';
    const sortedEspera = [...e].sort((a, b) => {
      const aPriority = isTopEscalation(a.escalationState) ? 0 : 1;
      const bPriority = isTopEscalation(b.escalationState) ? 0 : 1;
      if (aPriority !== bPriority) return aPriority - bPriority;
      // Secondary sort: updatedAt desc (preserva ordenação original do servidor)
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    return { atendendo: a, espera: sortedEspera };
  }, [conversations]);

  // Badges das abas direto do agregado do servidor (mesmo where da listagem;
  // semântica de terminais espelhada em matchesTab). Undefined enquanto
  // carrega → abas sem badge (honesto, sem zero falso).
  const tabCounts: InboxTabCounts | undefined =
    counts === undefined
      ? undefined
      : {
          awaitingMe: counts.tabs.awaitingMe,
          awaitingAnyone: counts.tabs.awaitingAnyone,
          aiDriving: counts.tabs.aiDriving,
          all: counts.tabs.all,
        };

  // Contagem por status para o filter rail (design Brief 2B).
  const statusCounts = counts?.byStatus as Record<string, number> | undefined;

  // P1/conflict counts for banners
  const p1Count = conversations.filter(
    (c) => c.operationalPriority === 'p0' || c.operationalPriority === 'p1',
  ).length;
  // conflictCount real do enriquecimento B3 (nº de conversas com ≥1 ConflictAlert ativo).
  const conflictCount = conversations.filter((c) => (c.conflictCount ?? 0) > 0).length;

  // Empty state logic
  const hasAnyFilter = Boolean(
    filters.status ??
    filters.channel ??
    filters.operationMode ??
    filters.search ??
    filters.awaitingParty ??
    filters.intentFilter ??
    filters.commitmentFilter ??
    filters.sequenceFlag ??
    filters.metaSessionType ??
    filters.inGroup,
  );

  const emptyState: InboxEmptyState = isLoading
    ? 'loading'
    : conversations.length === 0
      ? hasAnyFilter
        ? 'empty-filter'
        : 'empty-no-conv'
      : null;

  const activeFilterLabels = buildActiveFilterLabels(filters);

  // Alvo B round2 — contagem honesta: o badge da aba anuncia o TOTAL do
  // servidor; a lista mostra uma página. O pagehead declara os dois («X de Y
  // carregadas») para o operador saber o que está vendo. Aba sem agregado
  // (team-queue) ou total ≤ carregado: texto histórico.
  const tabTotalMap: Partial<Record<InboxTab, number | undefined>> = {
    'awaiting-me': tabCounts?.awaitingMe,
    'awaiting-anyone': tabCounts?.awaitingAnyone,
    'ai-driving': tabCounts?.aiDriving,
    all: tabCounts?.all,
  };
  const loadedPrefix = buildLoadedPrefix(conversations.length, tabTotalMap[currentTab]);

  // Direção no vazio (fricção 1 do teste do TL): aba do operador vazia + fila
  // do time com conversas ⇒ o empty-state aponta para «Aguardando atendente»
  // com a contagem real do agregado.
  const awaitingHint =
    currentTab === 'awaiting-me' && (tabCounts?.awaitingAnyone ?? 0) > 0
      ? { count: tabCounts?.awaitingAnyone ?? 0, onGo: () => setTab('awaiting-anyone') }
      : undefined;

  return (
    <div className="flex flex-col h-full">
      {/* Pagehead (Tier 1) */}
      <div className="app-pagehead">
        <div className="app-pagehead__top">
          <h1 className="app-pagehead__title">Listagem de Conversas</h1>
          <span className="app-pagehead__sub">
            {loadedPrefix}
            {buildPageScopeLabel(conversations.length, tabTotalMap[currentTab])}
            {conversations.length} ativas · {atendendo.length} em atendimento · {espera.length} em
            espera
          </span>
          <div className="app-pagehead__actions">
            <span className="kbd">?</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              atalhos
            </span>
          </div>
        </div>
      </div>

      {/* Banners */}
      <InboxBanners
        p1Count={p1Count}
        conflictCount={conflictCount}
        overloadCount={atendendo.length}
        onOpenP1={() => setSortOption('urgency')}
      />

      {/* Tabs */}
      <InboxTabs activeTab={currentTab} counts={tabCounts} onTabChange={setTab} />

      {/* Toolbar */}
      <InboxToolbar
        filters={filters}
        activeFilters={activeFilterLabels}
        sortOption={sortOption}
        onSortChange={setSortOption}
        onSearchChange={(search) => setFilter('search', search || undefined)}
        onClearFilters={clearFilters}
        onRefresh={() => void refetch()}
      />

      {/* List + Filter rail */}
      {isError && (
        <div className="p-6 text-center text-text-secondary text-sm">
          Não foi possível carregar conversas. Tente novamente.
        </div>
      )}
      {!isError && (
        <div className="flex flex-1 min-h-0">
          <div className="flex-1 min-w-0 overflow-auto">
            {/* Alvo A round2 — buscar um nome responde com a PESSOA (total
                real + recentes inline), acima dos resultados de conversa.
                Self-contido: gateia cap person-read + fetch por dentro. */}
            <InboxPersonSearchResults search={activeSearch} />
            <InboxConvList
              atendendo={atendendo}
              espera={espera}
              esperaCollapsed={esperaCollapsed}
              onToggleEspera={() => setEsperaCollapsed((c) => !c)}
              emptyState={emptyState}
              onSelect={(id) => navigate(`/atendimento/conversation/${id}`)}
              onClaim={(id) => claimMutation.mutate(id)}
              onClearFilters={clearFilters}
              groupByPersonEnabled={groupByPersonEnabled}
              {...(awaitingHint !== undefined && { awaitingHint })}
            />
          </div>
          <InboxFiltersRail
            filters={filters}
            onSetFilter={setFilter}
            onClear={clearFilters}
            statusCounts={statusCounts}
          />
        </div>
      )}
    </div>
  );
}
